import React from 'react';
import { ItineraryTypes } from '../types';
import { SvgIcon } from 'ui/SvgIcon';
import PointAIcon from '../icons/PointA.component.svg';
import { StayBadge } from 'ui/StayBadge/StayBadge';

export interface IItineraryOverviewItemProps {
  item: ItineraryTypes['ItineraryItem'];
  isLast?: boolean;
  allItems?: ItineraryTypes['ItineraryItem'][];
  syntheticAccommodations?: ItineraryTypes['ItineraryItem'][];
}

export const ItineraryOverviewItem = ({
  item,
  isLast = false,
  allItems = [],
  syntheticAccommodations,
}: IItineraryOverviewItemProps) => {
  const renderItemContent = () => {
    const numberOfAdults = item.numberOfAdults || 0;
    const agesOfAllChildren = item.agesOfAllChildren || [];

    switch (item.itemType) {
      case 'transfer':
        return (
          <div className="flex justify-between items-center">
            <span className="overview-row-title font-hurmegeometric-sans text-14px text-flint">{item.title}</span>
            {(numberOfAdults > 0 || agesOfAllChildren.length > 0) && (
              <span className="font-hurmegeometric-sans text-13px text-flint ml-4 hidden md:flex">
                {numberOfAdults > 0 && `${numberOfAdults} ADULT`}
                {numberOfAdults > 0 && agesOfAllChildren.length > 0 && ' '}
                {agesOfAllChildren.length > 0 && `${agesOfAllChildren.length} CHILD`}
              </span>
            )}
          </div>
        );

      case 'accommodation':
        return (
          <div className="flex justify-between items-center">
            <span className="overview-row-title font-hurmegeometric-sans text-14px text-flint">{item.title}</span>
            {(numberOfAdults > 0 || agesOfAllChildren.length > 0) && (
              <span className="font-hurmegeometric-sans text-13px text-flint ml-4 hidden md:flex">
                {numberOfAdults > 0 && `${numberOfAdults} ADULT`}
                {numberOfAdults > 0 && agesOfAllChildren.length > 0 && ' '}
                {agesOfAllChildren.length > 0 && `${agesOfAllChildren.length} CHILD`}
              </span>
            )}
          </div>
        );

      case 'groundService':
        return (
          <div className="flex justify-between items-center">
            <span className="overview-row-title font-hurmegeometric-sans text-14px text-flint">{item.title}</span>
            {(numberOfAdults > 0 || agesOfAllChildren.length > 0) && (
              <span className="font-hurmegeometric-sans text-13px text-flint ml-4 hidden md:flex">
                {numberOfAdults > 0 && `${numberOfAdults} ADULT`}
                {numberOfAdults > 0 && agesOfAllChildren.length > 0 && ' '}
                {agesOfAllChildren.length > 0 && `${agesOfAllChildren.length} CHILD`}
                {/* {item.ancillary_productRestrictions_minimumAge && ` • MEAL INCLUDED`} */}
              </span>
            )}
          </div>
        );

      case 'bespoke':
        return (
          <div className="flex justify-between items-center">
            <span className="overview-row-title font-hurmegeometric-sans text-14px text-flint">{item.title}</span>
            {(numberOfAdults > 0 || agesOfAllChildren.length > 0) && (
              <span className="font-hurmegeometric-sans text-13px text-flint ml-4 hidden md:flex">
                {numberOfAdults > 0 && `${numberOfAdults} ADULT`}
                {numberOfAdults > 0 && agesOfAllChildren.length > 0 && ' '}
                {agesOfAllChildren.length > 0 && `${agesOfAllChildren.length} CHILD`}
              </span>
            )}
          </div>
        );

      case 'hotelMarker':
        // Calculate rooms and guests for this hotel
        let roomCount = 0;
        let totalAdults = 0;
        let totalChildren = 0;

        // Check if this is a synthetic hotel marker with pre-grouped accommodations
        if (syntheticAccommodations) {
          syntheticAccommodations.forEach(accommodation => {
            roomCount++;
            totalAdults += accommodation.numberOfAdults || 0;
            totalChildren += (accommodation.agesOfAllChildren || []).length;
          });
        } else {
          // Fallback to original logic for non-synthetic markers
          const hotelIndex = allItems.findIndex(i => i.uuid === item.uuid);
          for (let i = hotelIndex + 1; i < allItems.length; i++) {
            const currentItem = allItems[i];
            if (currentItem.itemType === 'hotelMarker') break;
            if (currentItem.itemType === 'accommodation') {
              roomCount++;
              totalAdults += currentItem.numberOfAdults || 0;
              totalChildren += (currentItem.agesOfAllChildren || []).length;
            }
          }
        }

        const totalGuests = totalAdults + totalChildren;

        return (
          <div className="flex justify-between items-center">
            <span className="overview-row-title font-hurmegeometric-sans text-14px text-flint">{item.title}</span>
            <div className="hidden md:flex items-center gap-10px">
              {item.date && item.endDate && (
                <StayBadge
                  startDate={item.date}
                  endDate={item.endDate}
                  hideDates={true}
                  hideNights={false}
                  className="!gap-0"
                />
              )}
              {totalGuests > 0 && (
                <span className="font-hurmegeometric-sans text-13px text-flint uppercase">
                  {totalGuests} GUEST{totalGuests !== 1 ? 'S' : ''}
                </span>
              )}
              {roomCount > 0 && (
                <span className="font-hurmegeometric-sans text-13px text-flint uppercase">
                  {roomCount} ROOM{roomCount > 1 ? 'S' : ''}
                </span>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="flex justify-between items-center">
            <span className="font-pt-sans text-16px text-black">{item.title}</span>
          </div>
        );
    }
  };

  // Don't show destination markers in the overview items
  if (item.itemType === 'destinationMarker') {
    return null;
  }

  return (
    // <div className="grid grid-cols-[60px_20px_1fr] gap-[30px]"> <-- this was for the time col
    <div className="grid grid-cols-[20px_1fr] gap-[10px] content-start">
      {/* Time column */}
      {/* <div className="flex items-start">
        {item.time && <span className="font-hurmegeometric-sans text-12px text-flint uppercase">{item.time}</span>}
      </div> */}

      {/* Icon and line column */}
      <div className="flex flex-col items-center relative">
        <SvgIcon IconComponent={PointAIcon} className="w-[26px] fill-none stroke-none z-10 -translate-y-[4px]" />
        {!isLast && (
          // this is the "vertical line" that runs between the points
          <div className="absolute top-[26px] left-[50%] h-[30px] border-l-2 border-teal-100 border-dotted" />
        )}
      </div>

      {/* Content column */}
      <div className="pb-20px">{renderItemContent()}</div>
    </div>
  );
};
